// components/OnboardingTour.tsx — 4-screen first-run carousel explaining the
// current conversational-first product sections before the student lands on
// Il Prof.
// Shown exactly once per browser profile (gated by localStorage).
//
// History:
//  - W4.5 Session 3: Studia leads the carousel per §19 R17 — Studia is the
//    top-level surface above Allena, so the tour introduces it first.
//  - W4.6 (2026-04-29): Simula collapsed into Allena as an in-mode CTA.
//  - 2026-05-06 follow-up: the tour now mirrors the conversational-first IA:
//    Il Prof, Progressi, Simula Esame, Appendice. Studia remains an internal
//    lesson surface, not an onboarding destination.
//
// Keyboard: Right/Enter advances, Left goes back, Escape skips. Click "Salta"
// at any time to dismiss.

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTokens } from '../state/theme';
import { Screen } from './Screen';
import { MODE_ICONS, type ModeKey } from './modeIcons';

export const TOUR_SEEN_KEY = 'asc_onboarding_tour_seen_v1';

export function hasSeenTour(): boolean {
  try { return localStorage.getItem(TOUR_SEEN_KEY) === '1'; } catch { return true; }
}

function markTourSeen(): void {
  try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch { /* noop */ }
}

interface Slide {
  /** MODE_ICONS key for the slide's line icon (A9: no emoji in chrome). */
  icon:        ModeKey;
  title:       string;
  body:        string;
  bodySecondary?: string;
}

const SLIDES: Slide[] = [
  {
    icon: 'chiedi',
    title: 'Il Prof — il tuo tutor',
    body:  'Parti da una domanda, un dubbio o un argomento. Il Prof ti risponde, ti fa ragionare e poi ti porta alla pratica giusta.',
    bodySecondary: 'Puoi scrivere, dettare la domanda o ascoltare la risposta quando il browser lo supporta.',
  },
  {
    icon: 'progresso',
    title: 'Progressi — guarda dove sei',
    body:  'Trovi la tua preparazione argomento per argomento, la striscia e i punti da rinforzare.',
    bodySecondary: 'Da qui puoi tornare dal Prof con un argomento preciso, senza cercarlo da zero.',
  },
  {
    icon: 'simula',
    title: 'Simula Esame — prova i 20 minuti',
    body:  'Quando vuoi misurarti, fai una simulazione da 30 domande e controlli subito dove migliorare.',
    bodySecondary: 'È separata dal dialogo col Prof, così puoi provarla quando ti serve un vero check esame.',
  },
  {
    icon: 'appendice',
    title: 'Appendice — il tuo libro di riferimento',
    body:  'Tutti gli argomenti del Codice della Strada in un solo posto, organizzati per tema. Quando vuoi rivedere una regola o cercare un concetto specifico, l\'Appendice è lì — sempre nella barra in basso.',
    bodySecondary: 'Non devi memorizzarla: è una rete di sicurezza. La useremo insieme quando serve.',
  },
];

export function OnboardingTour({ onDone }: { onDone: () => void }): JSX.Element {
  const tokens = useTokens();
  const [idx, setIdx] = useState(0);
  const total = SLIDES.length;
  const slide = SLIDES[idx];
  const isLast = idx === total - 1;

  // B8 (V29 S2, go-signal middle path): the tour counts as SEEN on the first
  // meaningful interaction (advance / back / Salta / Escape), not on show.
  // Closing the tab untouched at slide 1 lets the tour return next visit; any
  // interaction ends it forever (preserves the 2026-07-07 anti-replay intent
  // without eating the tour for students who never saw slide 2).
  function markInteracted() {
    markTourSeen();
  }

  function dismiss() {
    markTourSeen();
    onDone();
  }

  function advance() {
    markInteracted();
    if (isLast) dismiss();
    else setIdx((i) => Math.min(i + 1, total - 1));
  }

  function back() {
    markInteracted();
    setIdx((i) => Math.max(i - 1, 0));
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); dismiss(); }
      else if (e.key === 'Enter' || e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); advance(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  return (
    <Screen>
      <div
        className="asc-fade-in"
        style={{
          // R2-5: the tour is a 3-region flex column that ALWAYS fits the
          // viewport — Salta (top) and the controls (bottom) are pinned as
          // non-shrinking, and only the middle slide body scrolls if its
          // content is tall on a short phone. `minHeight: 0` lets this column
          // (a flex child of Screen) actually shrink so the non-shrinking
          // top/bottom rows can never be pushed off-screen (the original bug:
          // Salta required scrolling on a 390x844 phone).
          flex:           1,
          minHeight:      0,
          display:        'flex',
          flexDirection:  'column',
          padding:        '16px 20px 24px',
        }}
      >
        {/* Top row: Salta link, aligned right so it's in a predictable spot.
            Pinned (flexShrink: 0) so it stays visible at the top regardless of
            slide-body height. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button
            onClick={dismiss}
            aria-label="Salta la presentazione"
            style={{
              background:   'transparent',
              border:       'none',
              padding:      '10px 12px',
              cursor:       'pointer',
              color:        tokens.textMuted,
              fontFamily:   tokens.fonts.body,
              fontSize:     13,
              minHeight:    44,
            }}
          >
            Salta
          </button>
        </div>

        {/* Slide body — centered vertically, wraps comfortably on mobile. It is
            the flexible middle region: `minHeight: 0` + `overflowY: auto` so a
            tall slide scrolls INSIDE this area instead of pushing the pinned
            Salta/controls out of the viewport. */}
        <div
          style={{
            flex:           1,
            minHeight:      0,
            overflowY:      'auto',
            display:        'flex',
            flexDirection:  'column',
            alignItems:     'center',
            justifyContent: 'center',
            gap:            18,
            maxWidth:       440,
            margin:         '0 auto',
            width:          '100%',
            textAlign:      'center',
          }}
        >
          {/* A9 (V29 S2): brand line icon on a gradient squircle — replaces
              the emoji hero (design-system rule: no emoji in chrome). */}
          <div
            style={{
              width:          72,
              height:         72,
              flexShrink:     0,
              borderRadius:   22,
              background:     tokens.brandGradient,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              boxShadow:      '0 8px 22px rgba(105, 67, 255, 0.28)',
            }}
            aria-hidden="true"
          >
            {MODE_ICONS[slide.icon]('#ffffff', 34)}
          </div>
          <h1
            style={{
              fontFamily:    tokens.fonts.serif,
              fontSize:      26,
              fontWeight:    700,
              color:         tokens.text,
              margin:        0,
              letterSpacing: -0.4,
              lineHeight:    1.2,
            }}
          >
            {slide.title}
          </h1>
          <p
            style={{
              fontFamily:    tokens.fonts.body,
              fontSize:      16,
              color:         tokens.textMuted,
              margin:        0,
              lineHeight:    1.55,
            }}
          >
            {slide.body}
          </p>
          {slide.bodySecondary && (
            <p
              style={{
                fontFamily:    tokens.fonts.body,
                fontSize:      14,
                color:         tokens.textFaint,
                margin:        0,
                lineHeight:    1.55,
              }}
            >
              {slide.bodySecondary}
            </p>
          )}
        </div>

        {/* Dots indicator + primary button — pinned (flexShrink: 0) so the
            Avanti/Indietro controls stay visible at the bottom on every
            viewport, paired with the pinned Salta at the top. */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, flexShrink: 0, paddingTop: 12 }}>
          <div style={{ display: 'flex', gap: 8 }} aria-hidden="true">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                style={{
                  width:         i === idx ? 22 : 8,
                  height:        8,
                  borderRadius:  999,
                  background:    i === idx ? tokens.accent : tokens.lineStrong,
                  transition:    'width 180ms ease, background 180ms ease',
                }}
              />
            ))}
          </div>

          <div
            style={{
              display:   'flex',
              gap:       10,
              width:     '100%',
              maxWidth:  440,
            }}
          >
            {idx > 0 && (
              <button
                onClick={back}
                style={{
                  background:    'transparent',
                  color:         tokens.textMuted,
                  border:        `1px solid ${tokens.line}`,
                  borderRadius:  tokens.radius.pill,
                  padding:       '14px 18px',
                  cursor:        'pointer',
                  fontFamily:    tokens.fonts.body,
                  fontSize:      15,
                  fontWeight:    500,
                  minHeight:     44,
                }}
              >
                Indietro
              </button>
            )}
            <button
              onClick={advance}
              autoFocus
              style={{
                flex:          1,
                background:    tokens.accent,
                // Light-only (V28 F1): tokens.mode always resolves 'light'.
                color:         '#fff',
                border:        'none',
                borderRadius:  tokens.radius.pill,
                padding:       '14px 20px',
                cursor:        'pointer',
                fontFamily:    tokens.fonts.body,
                fontSize:      15,
                fontWeight:    700,
                minHeight:     44,
                letterSpacing: -0.1,
              }}
            >
              {isLast ? 'Iniziamo!' : 'Avanti →'}
            </button>
          </div>

          <div
            aria-hidden="true"
            style={{
              fontFamily:    tokens.fonts.mono,
              fontSize:      10,
              color:         tokens.textFaint,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
            }}
          >
            {idx + 1} di {total}
          </div>
        </div>
      </div>
    </Screen>
  );
}
